// Plain CommonJS with no syntax that needs Babel runtime helpers (no object spread/rest), so the server tick can require it
// and the browser bundle can import it without the file turning into a mixed ESM/CommonJS module.
// Pure departure + daily-transfer planning. The database (gt_apply_ground_plan) recomputes riders, counts, times and
// capacity from its own rows and refuses a plan that disagrees, so this file only proposes.
const minute = 60000;
const time = (v) => (v == null || v === '' ? NaN : new Date(v).getTime());

// Departure pickup = flight time - airport buffer - drive time, rounded DOWN to the company's pickup step (never later).
function departurePickup(departureAt, international, settings = {}) {
    const buffer = international ? settings.departure_buffer_international_minutes ?? 180 : settings.departure_buffer_domestic_minutes ?? 120;
    const drive = settings.departure_drive_minutes ?? 45;
    const step = Math.max(1, settings.pickup_rounding_minutes ?? 5) * minute;
    return Math.floor((time(departureAt) - (buffer + drive) * minute) / step) * step;
}

// Smallest vehicle that fits everyone, else the largest one (the rest go in further vehicles).
function pickService(services, riders) {
    const usable = services.filter((s) => Number.isInteger(s.capacity) && s.capacity > 0)
        .sort((a, b) => a.capacity - b.capacity || String(a.service_id).localeCompare(String(b.service_id)));
    return usable.find((s) => s.capacity >= riders) || usable[usable.length - 1] || null;
}

function planDepartures(riders, services, settings = {}, now = Date.now()) {
    const windowMs = (settings.departure_grouping_minutes ?? 60) * minute;
    const waves = [], unassigned = [];
    const ordered = riders.slice().sort((a, b) => time(a.departure_at) - time(b.departure_at) || String(a.event_guest_id).localeCompare(String(b.event_guest_id)));
    const groups = new Map();
    for (const r of ordered) {
        // Migration 285: the database works out each rider's pickup (zone base + traffic chart, or the flat drive time when the zone
        // is unknown) and sends it as pickup_at; the flat formula is only the fallback for input without it.
        const given = time(r.pickup_at);
        const pick = Number.isFinite(given) ? given : departurePickup(r.departure_at, r.international, settings);
        if (!Number.isFinite(pick)) { unassigned.push({ guest: r.event_guest_id, reason: 'Add a departure date and time.' }); continue; }
        if (pick <= now) { unassigned.push({ guest: r.event_guest_id, reason: 'The departure pickup time has passed.' }); continue; }
        const key = `${r.airport}|${r.origin_address}`;
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(Object.assign({}, r, { pick }));
    }
    for (const list of groups.values()) {
        let current = null;
        list.forEach((r, i) => {
            if (!current || time(r.departure_at) - current.first_departure > windowMs || current.riders.length >= current.capacity) {
                const sameWindow = list.slice(i).filter((x) => time(x.departure_at) - time(r.departure_at) <= windowMs).length;
                const service = pickService(services, sameWindow);
                if (!service) { unassigned.push({ guest: r.event_guest_id, reason: 'Configure a service and passenger capacity.' }); current = null; return; }
                current = { direction: 'departure', service_id: service.service_id, capacity: service.capacity, airport: r.airport, origin: r.origin_address,
                    first_departure: time(r.departure_at), pickup: r.pick, riders: [] };
                waves.push(current);
            }
            current.riders.push({ event_guest_id: r.event_guest_id, count: 1 });
            current.pickup = Math.min(current.pickup, r.pick);
        });
    }
    return {
        waves: waves.map((w) => ({ direction: w.direction, service_id: w.service_id, airport: w.airport, origin: w.origin, riders: w.riders,
            pickup_at: new Date(w.pickup).toISOString(), signature: `d:${w.pickup}:${w.riders.map((r) => r.event_guest_id).sort().join(',')}` })),
        unassigned,
    };
}

function planDaily(moves, services, now = Date.now()) {
    const waves = [], unassigned = [];
    for (const m of moves) {
        let remaining = (m.count || 0) - (m.protected_count || 0);
        const pick = time(m.pickup_at);
        // No one counted is a planning gap, so a person is told. A move whose riders are all on protected rides, or whose time has passed, needs nothing.
        if ((m.count || 0) <= 0) {
            if (!(pick <= now)) unassigned.push({ move: m.move_id, reason: `Day ${m.day_number} transfer${m.origin ? ` from ${m.origin}` : ''} has nobody counted to ride, so no ride was built. Add guests, enter a head count or change Who rides.` });
            continue;
        }
        if (remaining <= 0) continue;
        if (!Number.isFinite(pick)) { unassigned.push({ move: m.move_id, reason: 'Enter the move time; no ride is built until it is set.' }); continue; }
        if (!(pick > now)) { unassigned.push({ move: m.move_id, reason: 'The move time has passed.' }); continue; }
        let index = 0;
        while (remaining > 0) {
            const service = pickService(services, remaining);
            if (!service) { unassigned.push({ move: m.move_id, reason: 'Configure a service and passenger capacity.' }); break; }
            const n = Math.min(remaining, service.capacity);
            waves.push({ direction: 'daily', service_id: service.service_id, move_id: m.move_id, origin: m.origin_address, destination: m.destination,
                pickup_at: new Date(pick).toISOString(), riders: [{ count: n, label: `Day ${m.day_number} riders` }],
                signature: `m:${m.move_id}:${pick}:${index}:${n}` });
            remaining -= n; index += 1;
        }
    }
    return { waves, unassigned };
}

// ctx = gt_ground_context / gt_ground_work output for one event + transportation company.
function planGround(ctx, now = Date.now()) {
    const services = ctx.services || [];
    const departures = planDepartures(ctx.departures || [], services, ctx.settings || {}, now);
    const daily = planDaily(ctx.daily || [], services, now);
    return { waves: departures.waves.concat(daily.waves), unassigned: departures.unassigned.concat(daily.unassigned) };
}

module.exports = { departurePickup, pickService, planDepartures, planDaily, planGround };
